import { Injectable, effect, inject, signal } from '@angular/core';
import { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { faBrightness, faMoon } from '@fortawesome/pro-regular-svg-icons';
import { CookieService } from './cookie.service';
import { BehaviorSubject } from 'rxjs';
import { WlColorContainer, WlContainer, WlThemeSettings } from './wl.service';
import {
  contentColorForOklch,
  darkenOklch,
  parseOklch,
  type Oklch,
} from '../utils/oklch';
import {
  buildThemeStylesheet,
  buildThemeTokens,
  sanitizeFontStylesheetUrl,
} from './theme-settings';

interface SidebarTokenValues {
  sb: string;
  content: string;
  focus: string;
  borderContent: string;
  icon: string;
}

export interface Theme {
  id: ThemeId;
  name: string;
  icon: IconDefinition;
  logo?: string;
  logoMobile?: string;
}

export type ThemeId = 'ehlight' | 'ehdark';
export type ThemeName = 'light' | 'dark';

/** Resolved CSS custom properties from the last visit, keyed by theme id. */
const THEME_VARS_CACHE_PREFIX = 'eh.theme-vars.v1.';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly THEME_COOKIE_NAME = 'theme';
  private readonly THEME_COOKIE_EXPIRY_DAYS = 365;
  private readonly cookieService = inject(CookieService);
  private readonly FALLBACK_PRIMARY_COLOR = 'oklch(54.95% 0.25 274.41)';
  private readonly styleElement: HTMLStyleElement;
  private readonly themeSettingsElement: HTMLStyleElement;
  private readonly customCssElement: HTMLStyleElement;
  private readonly colorCache = new Map<string, Oklch>();
  private v2SidebarIconDefault = '';
  private customCss = '';
  private fontLinkElement?: HTMLLinkElement;
  private appliedThemeTokens = new Set<string>();

  private readonly themeList: readonly Theme[] = [
    {
      id: 'ehlight',
      name: 'Light',
      icon: faBrightness,
      logo: '/assets/icon/logo.svg',
      logoMobile: '/assets/icon/logo-m.svg',
    },
    {
      id: 'ehdark',
      name: 'Dark',
      icon: faMoon,
      logo: '/assets/icon/logo-w.svg',
      logoMobile: '/assets/icon/logo-m-w.svg',
    },
  ] as const;

  private readonly _initialTheme = signal<Theme>(this.themeList[0]);
  private readonly _theme = signal<Theme>(this.themeList[0]);
  private readonly _primaryColor = signal<string>('');
  private readonly _wlColorConfig = signal<WlColorContainer | undefined>(
    undefined
  );
  private readonly _wlThemeSettings = signal<WlThemeSettings | undefined>(
    undefined
  );
  private readonly _wlContainer = signal<WlContainer | undefined>(undefined);
  private readonly primaryColorSubject = new BehaviorSubject<string>(
    this.getDefaultPrimaryColor()
  );

  readonly theme = this._theme.asReadonly();
  readonly primaryColor = this._primaryColor.asReadonly();
  readonly primaryColor$ = this.primaryColorSubject.asObservable();

  setWlContainer(wl: WlContainer | undefined): void {
    this._wlColorConfig.set(wl?.colors);
    this._wlThemeSettings.set(wl?.theme);
    this._wlContainer.set(wl);
    this.setCustomCss(wl?.customCss);

    if (wl) {
      if (wl.enableDarkMode === false) {
        this.cookieService.clearCookie(this.THEME_COOKIE_NAME, {
          crossSubdomain: true,
        });
      }
      this.initializeTheme(wl);
    } else {
      this.initializeTheme();
    }
  }

  getNextTheme(): Theme {
    const currentTheme = this._initialTheme();
    const wlContainer = this._wlContainer();

    // If dark mode is explicitly disabled, always return light theme
    if (wlContainer?.enableDarkMode === false) {
      return this.themeList[0];
    }

    return currentTheme.id === 'ehlight'
      ? this.themeList[1]
      : this.themeList[0];
  }

  constructor() {
    this.styleElement = document.createElement('style');
    this.styleElement.id = 'theme-service-styles';
    document.head.appendChild(this.styleElement);

    // Reuse existing elements so a second service instance cannot stack a
    // duplicate stylesheet that later writes would not clear.
    this.themeSettingsElement =
      document.querySelector<HTMLStyleElement>('style#wl-theme-settings') ??
      document.createElement('style');
    this.themeSettingsElement.id = 'wl-theme-settings';
    document.head.appendChild(this.themeSettingsElement);

    this.customCssElement =
      document.querySelector<HTMLStyleElement>('style#wl-custom-css') ??
      document.createElement('style');
    this.customCssElement.id = 'wl-custom-css';
    document.head.appendChild(this.customCssElement);

    this.initializeTheme();
    this.setupSystemThemeListener();

    effect(() => {
      queueMicrotask(() => {
        this.renderCustomCss(this.customCss);
        this.updateThemeColors(
          this._primaryColor() || this.getDefaultPrimaryColor(),
          this._theme().id
        );
      });
    });
  }

  initializeTheme(wl?: WlContainer): void {
    const savedTheme = this.cookieService.getCookie(this.THEME_COOKIE_NAME);
    const savedThemeObject = this.themeList.find(
      theme => theme.id === savedTheme
    );

    const { initialTheme, themeToApply } = this.determineInitialTheme(
      savedThemeObject,
      wl
    );

    this._initialTheme.set(initialTheme);
    this.applyTheme(themeToApply);

    const primaryColor = this.getPrimaryColorForTheme(themeToApply);
    this._primaryColor.set(primaryColor);
    this.primaryColorSubject.next(primaryColor);
    this.updateThemeColors(primaryColor, this._theme().id);
  }

  private determineInitialTheme(
    savedThemeObject: Theme | undefined,
    wl?: WlContainer
  ): {
    initialTheme: Theme;
    themeToApply: ThemeId;
  } {
    if (wl?.enableDarkMode === false) {
      return {
        initialTheme: this.themeList[0],
        themeToApply: 'ehlight',
      };
    }

    if (savedThemeObject) {
      return {
        initialTheme: savedThemeObject,
        themeToApply: savedThemeObject.id,
      };
    }

    // Check system theme when no cookie is saved and if dark mode is enabled
    const systemTheme = this.getSystemTheme();
    const systemThemeObject =
      this.themeList.find(theme => theme.id === systemTheme) ||
      this.themeList[0];

    return {
      initialTheme: systemThemeObject,
      themeToApply: systemTheme,
    };
  }

  private getSystemTheme(): ThemeId {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'ehdark'
      : 'ehlight';
  }

  private setupSystemThemeListener(): void {
    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', e => {
        // Only respond to system theme changes if no cookie is saved (following system preferences)
        // AND if dark mode is enabled in the white label configuration
        const savedTheme = this.cookieService.getCookie(this.THEME_COOKIE_NAME);
        const wlContainer = this._wlContainer();

        if (!savedTheme && wlContainer?.enableDarkMode !== false) {
          const newSystemTheme = e.matches ? 'ehdark' : 'ehlight';
          this.applyTheme(newSystemTheme);

          // Update the theme object to match the new system theme
          const newThemeObject = this.themeList.find(
            theme => theme.id === newSystemTheme
          );
          if (newThemeObject) {
            this._initialTheme.set(newThemeObject);
          }
        }
      });
  }

  private getDefaultPrimaryColor(): string {
    return (
      getComputedStyle(document.documentElement)
        .getPropertyValue('--color-primary')
        .trim() || this.FALLBACK_PRIMARY_COLOR
    );
  }

  setPrimaryColor(color?: string): void {
    if (!color) {
      color = this.FALLBACK_PRIMARY_COLOR; // can't use getDefaultPrimaryColor() because the computed style may be using an old theme
    }
    this._primaryColor.set(color);
    this.primaryColorSubject.next(color);
    this.updateThemeColors(color, this._theme().id);
  }

  /**
   * Agency-authored stylesheet. Theme tokens are written as inline styles on
   * the root element, so a `:root` override in this stylesheet must be
   * `!important` to take effect.
   */
  setCustomCss(css?: string): void {
    this.customCss = css?.trim() ?? '';
    this.renderCustomCss(this.customCss);
  }

  private renderCustomCss(css: string): void {
    if (this.customCssElement.textContent === css) {
      return;
    }

    this.customCssElement.textContent = css;
    if (css) {
      // Keep custom CSS last so it wins equal-specificity ties against lazily
      // loaded component styles.
      document.head.appendChild(this.customCssElement);
    }
  }

  /**
   * Structured agency theme. Like custom CSS it is v2-only, so classic mode
   * renders an empty stylesheet and drops the token overrides.
   */
  setThemeSettings(theme?: WlThemeSettings): void {
    this._wlThemeSettings.set(theme);

    const currentTheme = this._theme().id;
    this.updateThemeColors(
      this.getPrimaryColorForTheme(currentTheme),
      currentTheme
    );
  }

  setColorConfiguration(colorConfig?: WlColorContainer): void {
    this._wlColorConfig.set(colorConfig);

    const currentTheme = this._theme().id;
    const primaryColor = this.getPrimaryColorForTheme(currentTheme);
    this._primaryColor.set(primaryColor);
    this.primaryColorSubject.next(primaryColor);
    this.updateThemeColors(primaryColor, currentTheme);
  }

  private getPrimaryColorForTheme(theme: ThemeId): string {
    const colorConfig = this._wlColorConfig();

    if (theme === 'ehdark' && colorConfig?.primaryDark) {
      return colorConfig.primaryDark;
    }

    if (colorConfig?.primary) {
      return colorConfig.primary;
    }

    return this._primaryColor() || this.getDefaultPrimaryColor();
  }

  changeTheme(specificTheme?: ThemeId): void {
    const wlContainer = this._wlContainer();

    const themeToApply = this.determineThemeToApply(specificTheme, wlContainer);
    this.applyThemeChange(themeToApply);
  }

  private determineThemeToApply(
    specificTheme: ThemeId | undefined,
    wlContainer: WlContainer | undefined
  ): ThemeId {
    if (specificTheme) {
      if (wlContainer?.enableDarkMode === false && specificTheme === 'ehdark') {
        return 'ehlight';
      }

      if (this.themeList.some(theme => theme.id === specificTheme)) {
        return specificTheme;
      }
    }

    const nextThemeId = this.getNextThemeId();

    if (wlContainer?.enableDarkMode === false && nextThemeId === 'ehdark') {
      return 'ehlight';
    }

    return nextThemeId;
  }

  private getNextThemeId(): ThemeId {
    const currentMode = this._initialTheme();
    const currentIndex = this.themeList.findIndex(
      theme => theme.id === currentMode.id
    );
    const newIndex = (currentIndex + 1) % this.themeList.length;
    return this.themeList[newIndex].id;
  }

  private applyThemeChange(themeId: ThemeId): void {
    const themeObject = this.themeList.find(theme => theme.id === themeId);
    if (!themeObject) return;

    this._initialTheme.set(themeObject);

    this.cookieService.setCookie(
      this.THEME_COOKIE_NAME,
      themeId,
      this.THEME_COOKIE_EXPIRY_DAYS,
      {
        crossSubdomain: true,
      }
    );

    this.applyTheme(themeId);
  }

  private applyTheme(theme: ThemeId): void {
    const themeObject =
      this.themeList.find(t => t.id === theme) || this.themeList[0];

    this._theme.set(themeObject);
    document.documentElement.setAttribute('data-theme', theme);
    // Once per page load only: the cache exists to kill the boot flash, not to
    // replay stale values over a later theme or mode change.
    if (!this.restoredCachedThemeVars) {
      this.restoredCachedThemeVars = true;
      this.restoreCachedThemeVars(theme);
    }

    const primaryColor = this.getPrimaryColorForTheme(theme);
    this._primaryColor.set(primaryColor);
    this.primaryColorSubject.next(primaryColor);
    this.updateThemeColors(primaryColor, theme);
  }

  private updateThemeColors(primaryColor: string, theme: ThemeId): void {
    requestAnimationFrame(() => {
      const colorConfig = this._wlColorConfig();

      // Use primaryDark directly for dark theme if available, otherwise calculate dark color
      const darkColor =
        theme === 'ehdark' && colorConfig?.primaryDark
          ? colorConfig.primaryDark
          : darkenOklch(primaryColor);

      const highlightColor = this.getHighlightColor(primaryColor);
      const contentColor =
        contentColorForOklch(primaryColor) ?? 'var(--color-base-content)';

      // Create a single style update
      const styleUpdates = new Map<string, string>();

      // Agency colors
      styleUpdates.set('--color-agency', primaryColor);
      styleUpdates.set(
        '--color-agency-content',
        this.getContrastColor(primaryColor)
      );
      styleUpdates.set('--color-agency-focus', highlightColor);

      // Primary colors
      styleUpdates.set('--color-primary', primaryColor);
      styleUpdates.set('--color-primary-content', contentColor);
      styleUpdates.set('--p', primaryColor);
      styleUpdates.set('--pc', contentColor);
      styleUpdates.set('--a', primaryColor);
      styleUpdates.set('--ac', contentColor);
      styleUpdates.set('--pf', darkColor);
      styleUpdates.set('--af', darkColor);
      styleUpdates.set('--b', primaryColor);
      styleUpdates.set('--bc', contentColor);

      this.setButtonColors(primaryColor, darkColor, styleUpdates);

      const hasWlPrimary = !!(colorConfig?.primary || colorConfig?.primaryDark);
      this.applySidebarTokens(
        this.resolveSidebarTokens(
          primaryColor,
          contentColor,
          highlightColor,
          theme,
          hasWlPrimary
        ),
        styleUpdates
      );
      document.documentElement.classList.toggle('wl-themed', hasWlPrimary);

      this.applyThemeSettings(colorConfig, primaryColor, styleUpdates);
      this.applyStyleUpdates(styleUpdates);
    });
  }

  /**
   * Structured settings are applied last so an explicit agency choice wins
   * over the values derived from the primary color.
   */
  private applyThemeSettings(
    colorConfig: WlColorContainer | undefined,
    primaryColor: string,
    styleUpdates: Map<string, string>
  ): void {
    const settings = this._wlThemeSettings();
    const tokens = buildThemeTokens(colorConfig, settings, primaryColor);

    tokens.forEach((value, key) => styleUpdates.set(key, value));

    // A removed setting must release its token, otherwise the last saved
    // value would stay inline on the root element until a full reload.
    for (const key of this.appliedThemeTokens) {
      if (!tokens.has(key) && !styleUpdates.has(key)) {
        document.documentElement.style.removeProperty(key);
      }
    }
    this.appliedThemeTokens = new Set(tokens.keys());

    this.renderThemeSettings(buildThemeStylesheet(colorConfig, settings));
    this.applyFontStylesheet(
      sanitizeFontStylesheetUrl(settings?.fontStylesheetUrl)
    );
  }

  private renderThemeSettings(css: string): void {
    if (this.themeSettingsElement.textContent === css) {
      return;
    }

    this.themeSettingsElement.textContent = css;
    if (css) {
      // Keep the generated sheet ahead of custom CSS so an agency can still
      // override any of it by hand.
      document.head.insertBefore(
        this.themeSettingsElement,
        this.customCssElement
      );
    }
  }

  private applyFontStylesheet(url: string | undefined): void {
    if (!url) {
      this.fontLinkElement?.remove();
      this.fontLinkElement = undefined;
      return;
    }

    if (!this.fontLinkElement) {
      this.fontLinkElement =
        document.querySelector<HTMLLinkElement>('link#wl-theme-font') ??
        document.createElement('link');
      this.fontLinkElement.id = 'wl-theme-font';
      this.fontLinkElement.rel = 'stylesheet';
      document.head.appendChild(this.fontLinkElement);
    }

    if (this.fontLinkElement.getAttribute('href') !== url) {
      this.fontLinkElement.setAttribute('href', url);
    }
  }

  private setButtonColors(
    primaryColor: string,
    darkColor: string,
    styleUpdates: Map<string, string>
  ): void {
    styleUpdates.set('--color-border', primaryColor);
    styleUpdates.set('--btn-primary', primaryColor);
    styleUpdates.set('--btn-primary-hover', darkColor);
    styleUpdates.set('--btn-primary-active', darkColor);
    styleUpdates.set(
      '--btn-primary-disabled',
      this.getDisabledColor(primaryColor)
    );
  }

  private resolveSidebarTokens(
    primaryColor: string,
    contentColor: string,
    highlightColor: string,
    theme: ThemeId,
    hasWlPrimary: boolean
  ): SidebarTokenValues {
    const usePrimarySurface =
      hasWlPrimary || !this.hasSemanticToken('--color-base-200');

    if (usePrimarySurface) {
      return this.resolvePrimarySidebarTokens(
        primaryColor,
        contentColor,
        highlightColor,
        theme
      );
    }

    const tokens: SidebarTokenValues = {
      sb: this.resolveSemanticToken('--color-base-200', primaryColor),
      content: this.resolveSemanticToken('--color-base-content', contentColor),
      focus: this.resolveSemanticToken('--tone-200', highlightColor),
      borderContent: this.resolveSemanticToken(
        '--color-base-content',
        contentColor
      ),
      icon: '',
    };

    // Muted v2 icon is light-mode only; dark v2 matches --color-sb-content.
    tokens.icon =
      theme === 'ehlight'
        ? this.getV2SidebarIcon(tokens.content)
        : tokens.content;

    return tokens;
  }

  /**
   * Resolve the muted sidebar-icon color from `--color-sb-icon-muted`, an
   * immutable token ThemeService never overrides. Reading `--color-sb-icon`
   * directly is unreliable once this service has inline-set it.
   */
  private getV2SidebarIcon(sidebarContent: string): string {
    if (!this.v2SidebarIconDefault) {
      // Only cache a real value; a too-early empty read must not poison it.
      const muted = getComputedStyle(document.documentElement)
        .getPropertyValue('--color-sb-icon-muted')
        .trim();
      if (muted) {
        this.v2SidebarIconDefault = muted;
      }
    }

    return this.v2SidebarIconDefault || sidebarContent;
  }

  private resolvePrimarySidebarTokens(
    primaryColor: string,
    contentColor: string,
    highlightColor: string,
    theme: ThemeId
  ): SidebarTokenValues {
    const sb =
      theme === 'ehdark'
        ? `color-mix(in oklab, ${primaryColor} 67%, black)`
        : primaryColor;
    const focus =
      theme === 'ehdark'
        ? `color-mix(in oklab, ${primaryColor} 85%, white)`
        : highlightColor;

    return {
      sb,
      content: contentColor,
      focus,
      borderContent: contentColor,
      icon: contentColor,
    };
  }

  private resolveSemanticToken(cssVar: string, fallback: string): string {
    if (typeof document === 'undefined') {
      return fallback;
    }

    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(cssVar)
      .trim();

    return value || fallback;
  }

  private hasSemanticToken(cssVar: string): boolean {
    if (typeof document === 'undefined') {
      return false;
    }

    return !!getComputedStyle(document.documentElement)
      .getPropertyValue(cssVar)
      .trim();
  }

  private applySidebarTokens(
    tokens: SidebarTokenValues,
    styleUpdates: Map<string, string>
  ): void {
    styleUpdates.set('--color-sb', tokens.sb);
    styleUpdates.set('--color-sb-content', tokens.content);
    styleUpdates.set('--border-sb-content', tokens.borderContent);
    styleUpdates.set('--color-sb-focus', tokens.focus);
    // Always inline-set so the @theme default in `_tokens.scss` cannot leak
    // through on primary surfaces or after mode switches.
    styleUpdates.set('--color-sb-icon', tokens.icon);
  }

  private applyStyleUpdates(styleUpdates: Map<string, string>): void {
    const root = document.documentElement;
    styleUpdates.forEach((value, key) => {
      root.style.setProperty(key, value);
    });
    this.persistThemeVars(styleUpdates);
  }

  private themeVarsCacheKey(theme: string | null): string | undefined {
    return theme ? `${THEME_VARS_CACHE_PREFIX}${theme}` : undefined;
  }

  private restoredCachedThemeVars = false;

  private restoreCachedThemeVars(theme: ThemeId): void {
    const key = this.themeVarsCacheKey(theme);
    if (!key || typeof document === 'undefined') {
      return;
    }
    try {
      const raw = localStorage.getItem(key);
      if (!raw) {
        return;
      }
      const cached = JSON.parse(raw) as Record<string, string>;
      const root = document.documentElement;
      for (const [name, value] of Object.entries(cached)) {
        // Sidebar tokens only. `getDefaultPrimaryColor()` reads the computed
        // `--color-primary`, so restoring that would let the previous agency's
        // colour become this load's logical fallback and be persisted as such.
        if (name.startsWith('--color-sb') && typeof value === 'string') {
          root.style.setProperty(name, value);
        }
      }
    } catch {
      // Unreadable or malformed cache: the recompute still lands.
    }
  }

  private persistThemeVars(styleUpdates: Map<string, string>): void {
    const key = this.themeVarsCacheKey(
      document.documentElement.getAttribute('data-theme')
    );
    if (!key) {
      return;
    }
    try {
      localStorage.setItem(
        key,
        JSON.stringify(Object.fromEntries(styleUpdates))
      );
    } catch {
      // Private mode or a full quota: colours still resolve at runtime.
    }
  }

  private parseOklchColor(color: string): Oklch | null {
    const cached = this.colorCache.get(color);
    if (cached) return cached;

    const parsed = parseOklch(color);
    if (!parsed) return null;

    this.colorCache.set(color, parsed);
    return parsed;
  }

  private getHighlightColor(color: string): string {
    const oklch = this.parseOklchColor(color);
    if (!oklch) return 'var(--color-base-200)';

    const highlightLightness = Math.min(100, oklch.l + 5);
    const highlightChroma = Math.max(0, oklch.c);

    return `oklch(${highlightLightness}% ${highlightChroma} ${oklch.h})`;
  }

  private getContrastColor(color: string): string {
    const oklch = this.parseOklchColor(color);
    if (!oklch) return 'var(--color-base-content)';

    return oklch.l > 60 ? 'var(--color-base-300)' : 'var(--color-base-100)';
  }

  private getDisabledColor(color: string): string {
    const oklch = this.parseOklchColor(color);
    if (!oklch) return color;

    const disabledLightness = Math.min(100, oklch.l + 20);
    const disabledChroma = Math.max(0, oklch.c * 0.5);

    return `oklch(${disabledLightness}% ${disabledChroma} ${oklch.h})`;
  }
}
